import React, { Component } from 'react';
import ColumnRenderer from '../../util/ColumnRenderer';
import ColumnsSelector from '../ColumnsSelector';
import AggregateOperation from '../../util/AggregateOperation';
import DatetimeButtonGroup from '../DatetimeButtonGroup';
import { GROUP_BY } from '../../util/enums/EnumLayers';
import {
  GROUP_BY_MODE,
  ADD_COLUMNS_MODE,
} from '../../util/enums/EnumAggregateMode';
import { MYSQL } from '../../util/enums/EnumDbType';
import { DATE, DATETIME } from '../../util/ColumnType';
import { DAY, dateOptions } from '../../util/enums/EnumDateTimeAggregation';

class ModalGroupBy extends Component {
  static modalType() {
    return GROUP_BY;
  }

  static reducer(modals) {
    return modals.groupByModal;
  }

  static title() {
    return 'Calculate new column';
  }

  static subTitle() {
    return (
      <span>
        Choose the calculation(s) you want to perform and how you want to group
        the results. The results will be added to a new column.
      </span>
    );
  }

  aggButton(label, onClick, key) {
    return (
      <button
        key={key}
        type="button"
        className="btn btn-outline-dark margin-bottom-right"
        onClick={onClick}
      >
        <span>{label}</span>
      </button>
    );
  }

  aggFunctions(columns, state, userInteraction, usedAliases, dbType) {
    const content = [];

    // existing aggregate functions
    state.aggFunctions.forEach((aF, index) => {
      const chosenFunction = this.aggButton(
        <span>
          {aF.aggregateOp().name()}({aF.columnName().name()})
        </span>,
        userInteraction({ section: 'removeAgg', aggregateFunction: aF }),
        index,
      );
      content.push(chosenFunction);
    });

    // aggregate function builder
    const aggOps = AggregateOperation.all(dbType)
      .filter((ao) =>
        ao
          .supportedTypes()
          .some((t) => columns.some((c) => c.type().type() === t)),
      )
      .filter(
        (ao) =>
          ao.name() !==
          'median' /* median not supported as window function by postgres */,
      );
    if (!state.moreClicked) {
      // nothing to show
    } else if (!state.selectedOp) {
      // if an aggregate op hasn't yet been clicked, show the options
      const funcOptions = [];
      const advancedOptions = [];
      if (!state.countChecked) {
        funcOptions.push(
          <button
            key="count"
            type="button"
            className="btn btn-outline-dark"
            onClick={userInteraction({ section: 'countToggled' })}
          >
            count
          </button>,
        );
      }
      if (!state.percentChecked && dbType !== MYSQL) {
        funcOptions.push(
          <button
            key="percent"
            type="button"
            className="btn btn-outline-dark"
            onClick={userInteraction({ section: 'percentToggled' })}
          >
            percentage
          </button>,
        );
      }
      aggOps.forEach((ao, index) => {
        const props = { section: 'chooseOp', aggregateOperation: ao };
        (ao.isAdvanced() ? advancedOptions : funcOptions).push(
          <button
            key={index}
            type="button"
            className="btn btn-outline-dark"
            onClick={userInteraction(props)}
          >
            {ao.longName()}
          </button>,
        );
      });
      const chooseFunction = (
        <table key="agg-options">
          <tbody>
            <tr key="choose">
              <td />
              <td colSpan={2}>
                <div className="btn-group-vertical modal_options">
                  {funcOptions}
                </div>
                {!state.advancedClicked ? null : (
                  <div className="btn-group-vertical modal_options">
                    {advancedOptions}
                  </div>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      );
      content.push(chooseFunction);
    } else {
      // if an aggregate op has been chosen, show the columns that it can be applied to
      const renderer = new ColumnRenderer(
        'functionArg',
        userInteraction,
        usedAliases,
      );
      columns = columns.filter((c) =>
        state.selectedOp.supportedTypes().some((t) => t === c.type().type()),
      );
      const chooseColumn = (
        <table key="agg-cols">
          <tbody>
            <tr key="cols">
              <td className="padding-left-10">
                <input
                  type="checkbox"
                  checked
                  onChange={userInteraction({ section: 'undoMore' })}
                />
              </td>
              <td>
                <span>{state.selectedOp.name()} (</span>
              </td>
              <td>
                <ColumnsSelector
                  values={columns.map((c) => c.name())}
                  renderer={renderer}
                />
              </td>
              <td>)</td>
            </tr>
          </tbody>
        </table>
      );
      content.push(chooseColumn);
    }

    const countStar = !state.countChecked
      ? null
      : this.aggButton(
          'count',
          userInteraction({ section: 'countToggled' }),
          'cnt',
        );
    const percentStar = !state.percentChecked
      ? null
      : this.aggButton(
          'percentage',
          userInteraction({ section: 'percentToggled' }),
          'pcnt',
        );
    const more = state.moreClicked
      ? null
      : this.aggButton(
          <span className="glyphicon glyphicon-plus" />,
          userInteraction({ section: 'moreClicked' }),
          'more',
        );

    return (
      <div>
        {countStar}
        {percentStar}
        {content}
        {more}
      </div>
    );
  }

  groupBy(columns, state, userInteraction, usedAliases) {
    const selectedColumns = columns.filter((c) =>
      state.selected.some((aggCol) => aggCol.columnName().equals(c.name())),
    );
    columns = columns.filter(
      (c) =>
        !state.selected.some((aggCol) => aggCol.columnName().equals(c.name())),
    ); // don't let user select same column twice
    columns = columns.filter((c) => c.type().operators()); // only allow aggregating on types that support comparison (e.g. json doesn't)

    const extractAggregation = (columnName) => {
      const column = columns.find((c) => c.name().equals(columnName));
      return column.type().type() === DATE || column.type().type() === DATETIME
        ? DAY
        : null;
    };
    const renderer = new ColumnRenderer(
      'addColumn',
      userInteraction,
      usedAliases,
      extractAggregation,
    );
    let options = (
      <ColumnsSelector
        values={columns.map((c) => c.name())}
        renderer={renderer}
      />
    );

    const selected = state.selected.map((aggCol) => {
      const props = { section: 'removeColumn', aggCol };
      const column = selectedColumns.find((c) =>
        c.name().equals(aggCol.columnName()),
      );
      if (!column) {
        return null;
      }

      const changeAgg = (aggregation) => ({
        section: 'changeAgg',
        aggCol,
        aggregation,
      });

      const aggName = aggCol.aggregation();

      const onClick = (aggType) => {
        const payload = changeAgg(aggType);
        userInteraction(payload)();
      };

      const choice =
        column.type().type() !== DATE &&
        column.type().type() !== DATETIME ? null : (
          <DatetimeButtonGroup
            handleClick={onClick}
            aggName={aggName}
            options={dateOptions}
          />
        );
      return (
        <div
          key={aggCol.toString()}
          className="btn-group display-flex float-left margin-bottom-right"
        >
          <button
            type="button"
            className="btn btn-outline-dark"
            onClick={userInteraction(props)}
          >
            {aggCol.name()}
          </button>
          {choice}
        </div>
      );
    });
    let emptySpace = <input type="text" className="form-control" disabled />;
    if (!state.plusClicked) {
      const props = { section: 'plusClicked' };
      emptySpace = (
        <button
          type="button"
          className="btn btn-outline-dark float-left"
          onClick={userInteraction(props)}
        >
          <span className="glyphicon glyphicon-plus" />
        </button>
      );
      if (options.length === 0) {
        emptySpace = null;
      }
      options = [];
    }
    return (
      <div>
        {selected}
        {emptySpace}
        {options}
      </div>
    );
  }

  render() {
    const { state, columns, userInteraction, usedAliases, dbType } = this.props;

    const groupBy = this.groupBy(columns, state, userInteraction, usedAliases);
    const aggFunctions = this.aggFunctions(
      columns,
      state,
      userInteraction,
      usedAliases,
      dbType,
    );

    function modeChange(e) {
      const props = { section: 'mode', newValue: e.currentTarget.value };
      userInteraction(props)();
    }

    let selectMode =
      !state.advancedClicked && state.mode === GROUP_BY_MODE ? (
        <a
          href="#"
          className="pull-right"
          onClick={userInteraction({ section: 'advanced' })}
        >
          advanced
        </a>
      ) : (
        <select
          value={state.mode}
          className="form-control pull-right agg-type-chooser"
          onChange={modeChange}
        >
          <option value={GROUP_BY_MODE}>Summarise result (default)</option>
          <option value={ADD_COLUMNS_MODE}>Add result as new column</option>
        </select>
      );
    if (dbType === MYSQL) {
      selectMode = null;
    }

    return (
      <div>
        <table className="fixed-width-table">
          <tbody>
            <tr>
              <td>Choose calculation(s)</td>
              <td>Group results by</td>
            </tr>
            <tr>
              <td>{aggFunctions}</td>
              <td>{groupBy}</td>
            </tr>
          </tbody>
        </table>
        {selectMode}
      </div>
    );
  }
}

export default ModalGroupBy;
